import { useMemo } from "react";

import { DateTime } from "luxon";
import {
  ChatBubbleLeftRightIcon,
  CurrencyPoundIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import { IPart } from "../../../models/Part";
import Field from "./StockField";

/**
 * Per-part buyer quotes / inquiries are not yet exposed by the API.
 * The optional `partQuotes` field has been added to `IPart` (see
 * `docs/pending-endpoints.md`). When the backend provides it, this
 * component will render the table; until then it shows an empty state
 * plus the part's current asking price for context.
 */
interface PartQuoteLite {
  id: string;
  created: string | null;
  amount: number | null;
  status?: string | null;
  buyerName?: string | null;
  channel?: string | null; // eBay, Phone, Email, etc.
  notes?: string | null;
}

const statusTone = (status?: string | null) => {
  switch ((status ?? "").toLowerCase()) {
    case "accepted":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    case "rejected":
    case "declined":
      return "bg-red-50 text-red-700 ring-red-200";
    case "expired":
      return "bg-gray-100 text-gray-600 ring-gray-200";
    case "pending":
    case "open":
      return "bg-blue-50 text-blue-700 ring-blue-200";
    default:
      return "bg-gray-100 text-gray-700 ring-gray-200";
  }
};

export default function StockQuotes({ part }: { part: IPart }) {
  const quotes = useMemo<PartQuoteLite[]>(() => {
    const raw = (part as IPart & { partQuotes?: PartQuoteLite[] }).partQuotes;
    return Array.isArray(raw) ? raw : [];
  }, [part]);

  const stats = useMemo(() => {
    const amounts = quotes
      .map((q) => q.amount)
      .filter((a): a is number => typeof a === "number" && a > 0);
    const total = amounts.length;
    const highest = total ? Math.max(...amounts) : null;
    const lowest = total ? Math.min(...amounts) : null;
    const avg = total ? amounts.reduce((s, n) => s + n, 0) / total : null;
    return { total, highest, lowest, avg };
  }, [quotes]);

  return (
    <div className="flex flex-col gap-4">
      <Card title="Quotes" bodyClassName="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Field label="Asking Price">
            {part.price ? PriceFormat(part.price) : "-"}
          </Field>
          <Field label="Quotes Received">{stats.total}</Field>
          <Field label="Highest Offer">
            {stats.highest != null ? PriceFormat(stats.highest) : "-"}
          </Field>
          <Field label="Average Offer">
            {stats.avg != null ? PriceFormat(stats.avg) : "-"}
          </Field>
        </div>

        {quotes.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
            <ChatBubbleLeftRightIcon
              className="mx-auto h-8 w-8 text-gray-300"
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-gray-700">
              No quotes recorded for this part
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Best offers, eBay messages, phone enquiries and emailed quotes
              will appear here once the backend exposes them.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                    Received
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                    Buyer
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                    Channel
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {quotes.map((q) => (
                  <tr key={q.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {formatDateTime(q.created, DateTime.DATETIME_SHORT, "-")}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {q.buyerName ?? <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {q.channel ?? <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                      <span className="inline-flex items-center gap-1">
                        <CurrencyPoundIcon
                          className="h-3.5 w-3.5 text-gray-400"
                          aria-hidden="true"
                        />
                        {q.amount != null ? PriceFormat(q.amount) : "-"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {q.status ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${statusTone(q.status)}`}
                        >
                          {q.status}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {q.notes ?? <span className="text-gray-400">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
